import React from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { Modal } from "antd";
import { displayBoardBasisHe } from "../displayBoardBasisHe";
import "../FlightHotelPassengersPage.css";
import { formatInternalFreeCancellationLabel } from "./hubwayzCancellationDisplay";
import { hideSupplierBrandFromUserText } from "../hideSupplierBrandFromUserText";

function guestBoardLabel(raw) {
  const label = displayBoardBasisHe(raw);
  return label || null;
}

function guestBoardSummary(raw) {
  const parts = String(raw || "")
    .split("·")
    .map((part) => guestBoardLabel(part))
    .filter(Boolean);
  return [...new Set(parts)].join(" · ") || null;
}

function guestStars(rating) {
  if (rating == null || String(rating).trim() === "") return null;
  const n = Number(rating);
  if (Number.isFinite(n) && n > 0) return `${n} כוכבים`;
  return String(rating);
}

function guestOccupancy(display) {
  const adults = Number(display?.adultsTotal) || 0;
  const children = Number(display?.childrenTotal) || 0;
  const rooms = Number(display?.roomsCount) || 0;
  const bits = [];
  if (rooms > 1) bits.push(`${rooms} חדרים`);
  if (adults === 1) bits.push("מבוגר אחד");
  else if (adults > 1) bits.push(`${adults} מבוגרים`);
  if (children === 1) bits.push("ילד אחד");
  else if (children > 1) bits.push(`${children} ילדים`);
  return bits.join(" · ") || null;
}

/**
 * First penalty of 0 → internal free-cancel deadline.
 * No date, no free-cancellation promise. Supplier currency is not shown as the price.
 */
function guestCancellation(cancellation) {
  const frames = Array.isArray(cancellation?.frames) ? cancellation.frames : [];
  const first = frames[0];
  const amount =
    first?.penalty && Number.isFinite(Number(first.penalty.amount))
      ? Number(first.penalty.amount)
      : null;
  if (amount === 0) {
    return formatInternalFreeCancellationLabel(first?.to);
  }
  if (amount != null && amount > 0) {
    return "ביטול בכפוף לדמי ביטול";
  }
  const type = String(cancellation?.type || "").toLowerCase();
  if (type.includes("non") && type.includes("refund")) return "לא ניתן לביטול";
  if (type.includes("refund")) return "ניתן לביטול";
  return null;
}

function SummaryRow({ label, value, ltr = false }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd dir={ltr ? "ltr" : undefined}>{value}</dd>
    </div>
  );
}

function StepMark({ status }) {
  if (status === "done") return <CheckCircleFilled />;
  if (status === "error") return <CloseCircleFilled />;
  if (status === "active") return <LoadingOutlined />;
  return <span className="usm-hwz-confirm-step__wait" aria-hidden />;
}

/**
 * Stays open until availability, price, and room terms are confirmed.
 * A failed server response stays in the dialog. It does not Book or charge.
 */
export function RoomConfirmModal({
  open,
  steps = [],
  failure = null,
  onRetry,
  onAcknowledge,
  onExpiredSearch,
  searchExpired = false,
  busy = false,
}) {
  return (
    <Modal
      open={open}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      title={null}
      width={440}
      className="usm-hwz-confirm-wait"
    >
      <div dir="rtl" className="usm-hwz-confirm-wait__body">
        <h2 className="usm-hwz-confirm-wait__title">אישור החדר</h2>
        <ol className="usm-hwz-confirm-steps">
          {steps.map((step) => (
            <li
              key={step.id}
              className={`usm-hwz-confirm-step usm-hwz-confirm-step--${step.status}`}
            >
              <span className="usm-hwz-confirm-step__mark">
                <StepMark status={step.status} />
              </span>
              <span>
                <strong>{step.label}</strong>
                <span>{step.detail}</span>
              </span>
            </li>
          ))}
        </ol>
        {failure ? (
          <div className="usm-hwz-confirm-error" role="alert">
            <p>{failure.message}</p>
            {failure.canAcknowledge ? (
              <button
                type="button"
                className="hpm-btn-primary usm-fh-prebook-retry"
                onClick={onAcknowledge}
                disabled={busy}
              >
                אישור המחיר המעודכן
              </button>
            ) : null}
            {failure.canRetry ? (
              searchExpired ? (
                <button
                  type="button"
                  className="hpm-btn-primary usm-fh-prebook-retry"
                  onClick={onExpiredSearch}
                >
                  חזרה לתוצאות וחיפוש מחדש
                </button>
              ) : (
                <button
                  type="button"
                  className="hrm-link usm-fh-prebook-retry"
                  onClick={onRetry}
                  disabled={busy}
                >
                  נסה שוב
                </button>
              )
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Guest-facing confirmation of the hotel stay.
 * The supplier call stays in the page; this only changes what is shown.
 */
export default function HubwayzPrebookConfirmPanel({
  normalized,
  loading = false,
  error = null,
  showDetails = true,
}) {
  if (loading || (!normalized && !error)) return null;

  if (error) {
    return (
      <section className="usm-hwz-stay-summary usm-hwz-stay-summary--err" role="alert" dir="rtl">
        <h2>לא הצלחנו לאשר את פרטי המלון</h2>
        <p>נסו שוב, או חזרו לבחירת החדר.</p>
      </section>
    );
  }

  const d = normalized.display || {};
  const cancellation = guestCancellation(d.cancellation);
  const remarks = (d.remarks || []).filter((r) => r?.text);

  const availabilityVerified = normalized.supplierConfirmed === true;

  const banner = availabilityVerified ? (
        <div className="usm-fh-prebook-status usm-fh-prebook-status--ok" role="status">
          <CheckCircleFilled aria-hidden />
          <div className="usm-fh-prebook-status__copy">
            <p className="usm-fh-prebook-status__title">
              זמינות החדר אושרה – ממשיכים לפרטי הנוסעים
            </p>
            <p className="usm-fh-prebook-status__subtitle">
              החדר זמין לתאריכים שבחרתם. מלאו את פרטי הנוסעים כדי להמשיך בהזמנה.
            </p>
          </div>
        </div>
      ) : (
        <p className="usm-hwz-stay-summary__alert" role="alert">
          לא כל פרטי המלון אושרו, ולכן לא ניתן להמשיך.
        </p>
      );

  if (!showDetails) return banner;

  return (
    <section className="usm-hwz-stay-summary" dir="rtl" aria-label="פרטי המלון">
      {banner}
      <h2>פרטי המלון</h2>
      <dl>
        <SummaryRow label="מלון" value={d.hotelName} />
        <SummaryRow label="כתובת" value={d.addressLine} />
        <SummaryRow label="דירוג" value={guestStars(d.rating)} />
        <SummaryRow label="חדר" value={d.roomSummary} />
        <SummaryRow label="בסיס אירוח" value={guestBoardSummary(d.boardSummary)} />
        <SummaryRow label="תפוסה" value={guestOccupancy(d)} />
        <SummaryRow label="ביטול" value={cancellation} />
      </dl>
      {remarks.length ? (
        <div className="usm-hwz-stay-summary__notes">
          <h3>הערות</h3>
          {remarks.map((r, i) => {
            const text = hideSupplierBrandFromUserText(r.text);
            if (!text) return null;
            return <p key={`${r.key || "note"}-${i}`}>{text}</p>;
          })}
        </div>
      ) : null}
    </section>
  );
}
